// SEO content data for all tool pages - features, how-to steps, and FAQ
// Used by SEOContentSection component and SEO.js structured data

export const seoContentData = {
  pdfConverter: {
    en: {
      features: [
        'Convert images (JPG, PNG) and text files (CSV, TXT, JSON, XML) to PDF instantly',
        'No file size limits — process large files with ease',
        'Your files are processed locally in your browser for maximum privacy',
        '100% free with no registration or email required',
      ],
      howTo: [
        { title: 'Upload Your File', description: 'Drag and drop your image or text file into the upload area, or click to browse.' },
        { title: 'Automatic Conversion', description: 'Your file is instantly converted to PDF format in your browser.' },
        { title: 'Download PDF', description: 'Click the Download button to save your converted PDF file.' },
      ],
      faq: [
        { q: 'What file types can I convert to PDF?', a: 'You can convert JPG, PNG images and text-based files like CSV, TXT, JSON, and XML to PDF format.' },
        { q: 'Is this PDF converter free?', a: 'Yes, PDFLove PDF Converter is completely free with no hidden costs, registration, or file limits.' },
        { q: 'Are my files secure?', a: 'Absolutely. All file processing happens locally in your browser. Your files are never uploaded to any server.' },
        { q: 'Can I convert multiple files at once?', a: 'Currently you can convert one file at a time. For batch conversion, simply repeat the process for each file.' },
      ],
    },
    fr: {
      features: [
        'Convertissez des images (JPG, PNG) et des fichiers texte (CSV, TXT, JSON, XML) en PDF instantanément',
        'Aucune limite de taille de fichier — traitez de gros fichiers facilement',
        'Vos fichiers sont traités localement dans votre navigateur pour une confidentialité maximale',
        '100 % gratuit, sans inscription ni e-mail requis',
      ],
      howTo: [
        { title: 'Téléchargez votre fichier', description: 'Glissez-déposez votre image ou fichier texte dans la zone, ou cliquez pour parcourir.' },
        { title: 'Conversion automatique', description: 'Votre fichier est instantanément converti au format PDF dans votre navigateur.' },
        { title: 'Téléchargez le PDF', description: 'Cliquez sur le bouton Télécharger pour enregistrer votre fichier PDF converti.' },
      ],
      faq: [
        { q: 'Quels types de fichiers puis-je convertir en PDF ?', a: 'Vous pouvez convertir des images JPG, PNG et des fichiers texte comme CSV, TXT, JSON et XML au format PDF.' },
        { q: 'Ce convertisseur PDF est-il gratuit ?', a: "Oui, le convertisseur PDF PDFLove est entièrement gratuit, sans frais cachés, inscription ou limite de fichier." },
        { q: 'Mes fichiers sont-ils sécurisés ?', a: 'Absolument. Tout le traitement se fait localement dans votre navigateur. Vos fichiers ne sont jamais téléchargés sur un serveur.' },
      ],
    },
  },

  pdfToPng: {
    en: {
      features: [
        'Convert each PDF page to a high-quality PNG image',
        'Preserve text clarity and image quality in the conversion',
        'Download individual pages or all pages as a ZIP file',
        'Works entirely in your browser — no upload to servers',
      ],
      howTo: [
        { title: 'Upload PDF', description: 'Select or drag and drop your PDF file into the upload area.' },
        { title: 'Convert Pages', description: 'Each page of your PDF is automatically converted to a PNG image.' },
        { title: 'Download Images', description: 'Download individual PNG images or all pages as a ZIP archive.' },
      ],
      faq: [
        { q: 'How do I convert PDF to PNG?', a: 'Simply upload your PDF file and each page will be automatically converted to a high-quality PNG image.' },
        { q: 'What is the quality of the PNG output?', a: 'The PNG images maintain the original quality of your PDF pages with clear text and sharp images.' },
        { q: 'Can I convert a multi-page PDF?', a: 'Yes, every page of your PDF will be converted to a separate PNG image. You can download them all as a ZIP.' },
      ],
    },
    fr: {
      features: [
        'Convertissez chaque page PDF en image PNG de haute qualité',
        'Préservez la clarté du texte et la qualité de l\'image',
        'Téléchargez des pages individuelles ou toutes les pages en fichier ZIP',
        'Fonctionne entièrement dans votre navigateur — aucun téléchargement vers des serveurs',
      ],
      howTo: [
        { title: 'Téléchargez le PDF', description: 'Sélectionnez ou glissez-déposez votre fichier PDF dans la zone.' },
        { title: 'Convertir les pages', description: 'Chaque page de votre PDF est automatiquement convertie en image PNG.' },
        { title: 'Télécharger les images', description: 'Téléchargez les images PNG individuelles ou toutes les pages en archive ZIP.' },
      ],
      faq: [
        { q: 'Comment convertir un PDF en PNG ?', a: 'Téléchargez simplement votre fichier PDF et chaque page sera automatiquement convertie en image PNG de haute qualité.' },
        { q: 'Quelle est la qualité du PNG en sortie ?', a: 'Les images PNG conservent la qualité originale de vos pages PDF.' },
      ],
    },
  },

  imageToPdf: {
    en: {
      features: [
        'Convert JPG, PNG and other images to PDF format',
        'Combine multiple images into a single PDF document',
        'Maintain original image quality and resolution',
        'Fast, free, and secure — no server uploads',
      ],
      howTo: [
        { title: 'Select Images', description: 'Choose or drag and drop your image files (JPG, PNG) into the upload area.' },
        { title: 'Auto Convert', description: 'Your images are automatically converted to PDF format.' },
        { title: 'Download PDF', description: 'Click Download to save the converted PDF to your device.' },
      ],
      faq: [
        { q: 'What image formats can I convert to PDF?', a: 'You can convert JPG (JPEG) and PNG images to PDF format using this tool.' },
        { q: 'Will image quality be reduced?', a: 'No, the original image quality and resolution are preserved in the PDF output.' },
        { q: 'Is there a file size limit?', a: 'There is no strict file size limit. Processing happens in your browser, so it depends on your device capability.' },
      ],
    },
    fr: {
      features: [
        'Convertissez des images JPG, PNG et autres au format PDF',
        'Combinez plusieurs images en un seul document PDF',
        'Conservez la qualité et la résolution originales de l\'image',
        'Rapide, gratuit et sécurisé — aucun téléchargement vers des serveurs',
      ],
      howTo: [
        { title: 'Sélectionnez les images', description: 'Choisissez ou glissez-déposez vos fichiers image (JPG, PNG) dans la zone.' },
        { title: 'Conversion automatique', description: 'Vos images sont automatiquement converties au format PDF.' },
        { title: 'Téléchargez le PDF', description: 'Cliquez sur Télécharger pour enregistrer le PDF converti.' },
      ],
      faq: [
        { q: 'Quels formats d\'image puis-je convertir en PDF ?', a: 'Vous pouvez convertir des images JPG (JPEG) et PNG au format PDF.' },
        { q: 'La qualité de l\'image sera-t-elle réduite ?', a: 'Non, la qualité et la résolution originales sont préservées dans le PDF.' },
      ],
    },
  },

  pdfToWord: {
    en: {
      features: [
        'Convert PDF documents to editable Word (DOCX) format',
        'Preserve formatting, fonts, and layout during conversion',
        'Edit converted documents in Microsoft Word or Google Docs',
        'Free online conversion with no software installation needed',
      ],
      howTo: [
        { title: 'Upload PDF', description: 'Select or drag and drop your PDF file into the converter.' },
        { title: 'Convert to Word', description: 'The tool extracts text and converts your PDF to DOCX format.' },
        { title: 'Download DOCX', description: 'Click Download to save the editable Word document.' },
      ],
      faq: [
        { q: 'How do I convert PDF to Word online?', a: 'Upload your PDF file to PDFLove and it will be automatically converted to an editable Word (DOCX) document.' },
        { q: 'Will the formatting be preserved?', a: 'The converter preserves text content and basic formatting. Complex layouts may require minor adjustments.' },
        { q: 'Can I edit the converted Word document?', a: 'Yes, the output DOCX file can be opened and edited in Microsoft Word, Google Docs, or any compatible word processor.' },
      ],
    },
    fr: {
      features: [
        'Convertissez des documents PDF au format Word (DOCX) modifiable',
        'Préservez la mise en forme, les polices et la mise en page',
        'Modifiez les documents convertis dans Microsoft Word ou Google Docs',
        'Conversion en ligne gratuite sans installation de logiciel',
      ],
      howTo: [
        { title: 'Téléchargez le PDF', description: 'Sélectionnez ou glissez-déposez votre fichier PDF dans le convertisseur.' },
        { title: 'Convertir en Word', description: "L'outil extrait le texte et convertit votre PDF au format DOCX." },
        { title: 'Téléchargez le DOCX', description: 'Cliquez sur Télécharger pour enregistrer le document Word modifiable.' },
      ],
      faq: [
        { q: 'Comment convertir un PDF en Word en ligne ?', a: 'Téléchargez votre fichier PDF sur PDFLove et il sera automatiquement converti en document Word (DOCX) modifiable.' },
        { q: 'La mise en forme sera-t-elle préservée ?', a: 'Le convertisseur préserve le contenu textuel et la mise en forme de base.' },
      ],
    },
  },

  pdfToExcel: {
    en: {
      features: [
        'Extract tables from PDF and convert to Excel spreadsheets',
        'Preserve data accuracy and table structure',
        'Output in standard XLSX format compatible with Excel and Google Sheets',
        'Process files securely in your browser',
      ],
      howTo: [
        { title: 'Upload PDF', description: 'Select or drag and drop your PDF containing tables.' },
        { title: 'Extract Data', description: 'Tables are automatically detected and extracted from the PDF.' },
        { title: 'Download Excel', description: 'Download the converted Excel (XLSX) spreadsheet.' },
      ],
      faq: [
        { q: 'Can I convert PDF tables to Excel?', a: 'Yes, PDFLove detects tables in your PDF and converts them into editable Excel spreadsheets.' },
        { q: 'What Excel format is used?', a: 'The output is in XLSX format, compatible with Microsoft Excel, Google Sheets, and LibreOffice Calc.' },
        { q: 'Is the data extraction accurate?', a: 'The tool accurately extracts tabular data. For best results, use PDFs with well-structured tables.' },
      ],
    },
    fr: {
      features: [
        'Extrayez les tableaux d\'un PDF et convertissez-les en feuilles de calcul Excel',
        'Préservez la précision des données et la structure des tableaux',
        'Format XLSX standard compatible avec Excel et Google Sheets',
        'Traitement sécurisé dans votre navigateur',
      ],
      howTo: [
        { title: 'Téléchargez le PDF', description: 'Sélectionnez ou glissez-déposez votre PDF contenant des tableaux.' },
        { title: 'Extraction des données', description: 'Les tableaux sont automatiquement détectés et extraits du PDF.' },
        { title: "Téléchargez l'Excel", description: 'Téléchargez la feuille de calcul Excel (XLSX) convertie.' },
      ],
      faq: [
        { q: 'Puis-je convertir des tableaux PDF en Excel ?', a: 'Oui, PDFLove détecte les tableaux dans votre PDF et les convertit en feuilles de calcul Excel modifiables.' },
      ],
    },
  },

  mergePDF: {
    en: {
      features: [
        'Combine multiple PDF files into one document',
        'Maintain the quality and formatting of all original PDFs',
        'Simple drag-and-drop interface',
        'Free with no page or file count limits',
      ],
      howTo: [
        { title: 'Select PDFs', description: 'Choose two or more PDF files to merge by clicking or dragging them.' },
        { title: 'Merge Files', description: 'Click the Merge button to combine all selected PDFs into one document.' },
        { title: 'Download Result', description: 'Download the merged PDF file to your device.' },
      ],
      faq: [
        { q: 'How do I merge PDF files online?', a: 'Upload two or more PDF files using PDFLove, click Merge, and download the combined PDF.' },
        { q: 'Is there a limit on the number of PDFs I can merge?', a: 'There is no strict limit. You can merge as many PDF files as your browser can handle.' },
        { q: 'Will the merged PDF lose quality?', a: 'No, the original quality and formatting of each PDF page is fully preserved.' },
      ],
    },
    fr: {
      features: [
        'Combinez plusieurs fichiers PDF en un seul document',
        'Conservez la qualité et la mise en forme de tous les PDF originaux',
        'Interface simple par glisser-déposer',
        'Gratuit sans limite de pages ou de fichiers',
      ],
      howTo: [
        { title: 'Sélectionnez les PDF', description: 'Choisissez deux ou plusieurs fichiers PDF à fusionner.' },
        { title: 'Fusionner les fichiers', description: 'Cliquez sur le bouton Fusionner pour combiner tous les PDF sélectionnés.' },
        { title: 'Téléchargez le résultat', description: 'Téléchargez le fichier PDF fusionné sur votre appareil.' },
      ],
      faq: [
        { q: 'Comment fusionner des fichiers PDF en ligne ?', a: 'Téléchargez deux ou plusieurs fichiers PDF, cliquez sur Fusionner et téléchargez le PDF combiné.' },
        { q: 'Y a-t-il une limite au nombre de PDF que je peux fusionner ?', a: "Il n'y a pas de limite stricte. Vous pouvez fusionner autant de fichiers PDF que votre navigateur peut gérer." },
      ],
    },
  },

  splitPDF: {
    en: {
      features: [
        'Split PDF documents into individual pages',
        'Extract specific pages from large PDF files',
        'Download each page as a separate PDF',
        'Fast processing entirely in your browser',
      ],
      howTo: [
        { title: 'Upload PDF', description: 'Select or drag and drop the PDF file you want to split.' },
        { title: 'Split Pages', description: 'Click Split to divide the PDF into individual pages.' },
        { title: 'Download Pages', description: 'Download the individual PDF pages you need.' },
      ],
      faq: [
        { q: 'How do I split a PDF into separate pages?', a: 'Upload your PDF to PDFLove, click Split, and each page becomes a separate downloadable PDF.' },
        { q: 'Can I extract specific pages?', a: 'Yes, after splitting you can download only the specific pages you need.' },
      ],
    },
    fr: {
      features: [
        'Divisez les documents PDF en pages individuelles',
        'Extrayez des pages spécifiques de grands fichiers PDF',
        'Téléchargez chaque page en tant que PDF séparé',
        'Traitement rapide entièrement dans votre navigateur',
      ],
      howTo: [
        { title: 'Téléchargez le PDF', description: 'Sélectionnez ou glissez-déposez le fichier PDF que vous souhaitez diviser.' },
        { title: 'Diviser les pages', description: 'Cliquez sur Diviser pour séparer le PDF en pages individuelles.' },
        { title: 'Téléchargez les pages', description: 'Téléchargez les pages PDF individuelles dont vous avez besoin.' },
      ],
      faq: [
        { q: 'Comment diviser un PDF en pages séparées ?', a: 'Téléchargez votre PDF sur PDFLove, cliquez sur Diviser, et chaque page devient un PDF téléchargeable séparé.' },
      ],
    },
  },

  pdfToGif: {
    en: {
      features: [
        'Convert PDF pages to animated GIF format',
        'Great for creating presentations and social media content',
        'Adjustable quality and frame settings',
        'No software needed — works in your browser',
      ],
      howTo: [
        { title: 'Upload PDF', description: 'Select or drag and drop your PDF file.' },
        { title: 'Convert to GIF', description: 'Click Convert to transform PDF pages into an animated GIF.' },
        { title: 'Download GIF', description: 'Preview and download the generated GIF file.' },
      ],
      faq: [
        { q: 'Can I convert a PDF to GIF?', a: 'Yes, PDFLove converts your PDF pages into an animated GIF image showing each page in sequence.' },
        { q: 'What is the output quality?', a: 'The GIF maintains good quality suitable for presentations and sharing on social media.' },
      ],
    },
    fr: {
      features: [
        'Convertissez les pages PDF au format GIF animé',
        'Idéal pour créer des présentations et du contenu pour les réseaux sociaux',
        'Qualité et paramètres d\'image ajustables',
        'Aucun logiciel nécessaire — fonctionne dans votre navigateur',
      ],
      howTo: [
        { title: 'Téléchargez le PDF', description: 'Sélectionnez ou glissez-déposez votre fichier PDF.' },
        { title: 'Convertir en GIF', description: 'Cliquez sur Convertir pour transformer les pages PDF en GIF animé.' },
        { title: 'Téléchargez le GIF', description: 'Prévisualisez et téléchargez le fichier GIF généré.' },
      ],
      faq: [
        { q: 'Puis-je convertir un PDF en GIF ?', a: 'Oui, PDFLove convertit vos pages PDF en image GIF animée.' },
      ],
    },
  },

  pdfToCsv: {
    en: {
      features: ['Extract tabular data from PDF to CSV format', 'Compatible with Excel, Google Sheets, and databases', 'Accurate table detection and extraction', 'Free and secure — no data leaves your browser'],
      howTo: [
        { title: 'Upload PDF', description: 'Select your PDF file containing tabular data.' },
        { title: 'Extract Data', description: 'Tables are automatically extracted and converted to CSV format.' },
        { title: 'Download CSV', description: 'Save the CSV file for use in spreadsheets or databases.' },
      ],
      faq: [
        { q: 'How do I extract data from PDF to CSV?', a: 'Upload your PDF with tables to PDFLove and the tool will automatically extract the data into CSV format.' },
        { q: 'What is CSV format used for?', a: 'CSV (Comma-Separated Values) is a universal data format used in spreadsheets, databases, and data analysis tools.' },
      ],
    },
    fr: {
      features: ['Extrayez les données tabulaires du PDF au format CSV', 'Compatible avec Excel, Google Sheets et les bases de données', 'Détection et extraction précises des tableaux', 'Gratuit et sécurisé'],
      howTo: [
        { title: 'Téléchargez le PDF', description: 'Sélectionnez votre fichier PDF contenant des données tabulaires.' },
        { title: 'Extraction des données', description: 'Les tableaux sont automatiquement extraits et convertis au format CSV.' },
        { title: 'Téléchargez le CSV', description: 'Enregistrez le fichier CSV.' },
      ],
      faq: [{ q: 'Comment extraire des données d\'un PDF en CSV ?', a: 'Téléchargez votre PDF avec des tableaux et l\'outil extraira automatiquement les données au format CSV.' }],
    },
  },

  pdfToJson: {
    en: {
      features: ['Convert PDF content to structured JSON format', 'Ideal for developers and data processing workflows', 'Extract text and metadata from PDF files', 'Process files locally for maximum security'],
      howTo: [
        { title: 'Upload PDF', description: 'Select or drag and drop your PDF file.' },
        { title: 'Convert to JSON', description: 'PDF content is parsed and structured into JSON format.' },
        { title: 'Download JSON', description: 'Download the JSON file for use in your applications.' },
      ],
      faq: [
        { q: 'Why convert PDF to JSON?', a: 'JSON is a lightweight data format widely used in web development and APIs, making PDF data easier to process programmatically.' },
        { q: 'What data is extracted?', a: 'The tool extracts text content, page information, and metadata from your PDF into structured JSON.' },
      ],
    },
    fr: {
      features: ['Convertissez le contenu PDF au format JSON structuré', 'Idéal pour les développeurs', 'Extrayez le texte et les métadonnées des fichiers PDF', 'Traitement local pour une sécurité maximale'],
      howTo: [
        { title: 'Téléchargez le PDF', description: 'Sélectionnez ou glissez-déposez votre fichier PDF.' },
        { title: 'Convertir en JSON', description: 'Le contenu PDF est analysé et structuré au format JSON.' },
        { title: 'Téléchargez le JSON', description: 'Téléchargez le fichier JSON.' },
      ],
      faq: [{ q: 'Pourquoi convertir un PDF en JSON ?', a: 'Le JSON est un format de données léger largement utilisé dans le développement web et les API.' }],
    },
  },

  pdfToPpt: {
    en: {
      features: ['Convert PDF files to PowerPoint (PPT) presentations', 'Each PDF page becomes a slide', 'Preserve visual layout and content', 'Free online conversion — no software needed'],
      howTo: [
        { title: 'Upload PDF', description: 'Select your PDF file to convert.' },
        { title: 'Convert to PPT', description: 'Each page is converted to a PowerPoint slide.' },
        { title: 'Download PPT', description: 'Save the PowerPoint presentation to your device.' },
      ],
      faq: [
        { q: 'Can I convert PDF to PowerPoint?', a: 'Yes, PDFLove converts each page of your PDF into a slide in a PowerPoint presentation.' },
        { q: 'Can I edit the slides after conversion?', a: 'Yes, the output PPT file can be opened and edited in PowerPoint or Google Slides.' },
      ],
    },
    fr: {
      features: ['Convertissez des fichiers PDF en présentations PowerPoint (PPT)', 'Chaque page PDF devient une diapositive', 'Préservez la mise en page visuelle et le contenu', 'Conversion en ligne gratuite'],
      howTo: [
        { title: 'Téléchargez le PDF', description: 'Sélectionnez votre fichier PDF à convertir.' },
        { title: 'Convertir en PPT', description: 'Chaque page est convertie en diapositive PowerPoint.' },
        { title: 'Téléchargez le PPT', description: 'Enregistrez la présentation PowerPoint.' },
      ],
      faq: [{ q: 'Puis-je convertir un PDF en PowerPoint ?', a: 'Oui, PDFLove convertit chaque page de votre PDF en diapositive PowerPoint.' }],
    },
  },

  pdfToRtf: {
    en: {
      features: ['Convert PDF to Rich Text Format (RTF)', 'RTF is compatible with virtually all word processors', 'Preserve text formatting and structure', 'Free and secure online conversion'],
      howTo: [
        { title: 'Upload PDF', description: 'Select your PDF file.' },
        { title: 'Convert to RTF', description: 'Text is extracted and formatted in RTF.' },
        { title: 'Download RTF', description: 'Save the RTF file.' },
      ],
      faq: [{ q: 'What is RTF format?', a: 'RTF (Rich Text Format) is a document format supported by almost all word processors including Word, WordPad, and TextEdit.' }],
    },
    fr: {
      features: ['Convertissez PDF en format RTF', 'RTF est compatible avec quasiment tous les traitements de texte', 'Préservez la mise en forme du texte', 'Conversion en ligne gratuite et sécurisée'],
      howTo: [
        { title: 'Téléchargez le PDF', description: 'Sélectionnez votre fichier PDF.' },
        { title: 'Convertir en RTF', description: 'Le texte est extrait et formaté en RTF.' },
        { title: 'Téléchargez le RTF', description: 'Enregistrez le fichier RTF.' },
      ],
      faq: [{ q: 'Qu\'est-ce que le format RTF ?', a: 'Le RTF est un format de document pris en charge par presque tous les traitements de texte.' }],
    },
  },

  pdfToPptx: {
    en: {
      features: ['Convert PDF to modern PPTX PowerPoint format', 'Compatible with PowerPoint 2007+ and Google Slides', 'Each page becomes an editable slide', 'No software installation required'],
      howTo: [
        { title: 'Upload PDF', description: 'Choose your PDF file.' },
        { title: 'Convert to PPTX', description: 'Pages are converted to PPTX slides.' },
        { title: 'Download PPTX', description: 'Download the PPTX presentation.' },
      ],
      faq: [{ q: 'What is the difference between PPT and PPTX?', a: 'PPTX is the modern XML-based format used by PowerPoint 2007 and later, offering better compatibility and smaller file sizes.' }],
    },
    fr: {
      features: ['Convertissez PDF au format PowerPoint PPTX moderne', 'Compatible avec PowerPoint 2007+ et Google Slides', 'Chaque page devient une diapositive modifiable', 'Aucune installation de logiciel requise'],
      howTo: [
        { title: 'Téléchargez le PDF', description: 'Choisissez votre fichier PDF.' },
        { title: 'Convertir en PPTX', description: 'Les pages sont converties en diapositives PPTX.' },
        { title: 'Téléchargez le PPTX', description: 'Téléchargez la présentation PPTX.' },
      ],
      faq: [{ q: 'Quelle est la différence entre PPT et PPTX ?', a: 'PPTX est le format XML moderne utilisé par PowerPoint 2007 et versions ultérieures.' }],
    },
  },

  pdfToTxt: {
    en: {
      features: ['Extract plain text from PDF documents', 'Clean text output without formatting', 'Ideal for text analysis and data extraction', 'Works with scanned and digital PDFs'],
      howTo: [
        { title: 'Upload PDF', description: 'Select your PDF file.' },
        { title: 'Extract Text', description: 'Text content is extracted from all PDF pages.' },
        { title: 'Download TXT', description: 'Save the plain text file.' },
      ],
      faq: [
        { q: 'How do I extract text from a PDF?', a: 'Upload your PDF to PDFLove and the tool will extract all readable text into a plain TXT file.' },
        { q: 'Will formatting be preserved?', a: 'TXT is a plain text format, so formatting like bold, italics, and fonts are removed. Only the raw text content is extracted.' },
      ],
    },
    fr: {
      features: ['Extrayez le texte brut des documents PDF', 'Sortie de texte propre sans mise en forme', 'Idéal pour l\'analyse de texte', 'Fonctionne avec les PDF numérisés et numériques'],
      howTo: [
        { title: 'Téléchargez le PDF', description: 'Sélectionnez votre fichier PDF.' },
        { title: 'Extraire le texte', description: 'Le contenu textuel est extrait de toutes les pages PDF.' },
        { title: 'Téléchargez le TXT', description: 'Enregistrez le fichier texte brut.' },
      ],
      faq: [{ q: 'Comment extraire du texte d\'un PDF ?', a: 'Téléchargez votre PDF sur PDFLove et l\'outil extraira tout le texte lisible dans un fichier TXT.' }],
    },
  },

  pdfToDocx: {
    en: {
      features: ['Convert PDF to modern DOCX Word format', 'Compatible with Word 2007+, Google Docs, and LibreOffice', 'Preserve document structure and text content', 'Free conversion with no file size limits'],
      howTo: [
        { title: 'Upload PDF', description: 'Select your PDF document.' },
        { title: 'Convert to DOCX', description: 'The PDF is converted to editable DOCX format.' },
        { title: 'Download DOCX', description: 'Save the Word document to your device.' },
      ],
      faq: [
        { q: 'What is the difference between DOC and DOCX?', a: 'DOCX is the modern XML-based Word format that offers better compatibility, smaller file sizes, and improved features.' },
        { q: 'Can I edit the converted document?', a: 'Yes, the DOCX output is fully editable in Microsoft Word, Google Docs, or any compatible editor.' },
      ],
    },
    fr: {
      features: ['Convertissez PDF au format Word DOCX moderne', 'Compatible avec Word 2007+, Google Docs et LibreOffice', 'Préservez la structure du document et le contenu textuel', 'Conversion gratuite sans limite de taille'],
      howTo: [
        { title: 'Téléchargez le PDF', description: 'Sélectionnez votre document PDF.' },
        { title: 'Convertir en DOCX', description: 'Le PDF est converti au format DOCX modifiable.' },
        { title: 'Téléchargez le DOCX', description: 'Enregistrez le document Word.' },
      ],
      faq: [{ q: 'Quelle est la différence entre DOC et DOCX ?', a: 'DOCX est le format Word XML moderne offrant une meilleure compatibilité et des fichiers plus petits.' }],
    },
  },

  compareText: {
    en: {
      features: ['Compare two text files side by side', 'Highlight differences with color coding', 'Instant comparison — no server processing', 'Support for any plain text content'],
      howTo: [
        { title: 'Enter Text', description: 'Paste or type text into two input fields.' },
        { title: 'Compare', description: 'Click Compare to highlight differences between the texts.' },
        { title: 'Review Results', description: 'Differences are highlighted with color for easy identification.' },
      ],
      faq: [
        { q: 'How does the text comparison work?', a: 'The tool compares two text inputs character by character and highlights additions, deletions, and changes.' },
        { q: 'Can I compare code files?', a: 'Yes, you can paste any text including source code, documents, or data files for comparison.' },
      ],
    },
    fr: {
      features: ['Comparez deux fichiers texte côte à côte', 'Surlignez les différences avec un code couleur', 'Comparaison instantanée — aucun traitement serveur', 'Supporte tout contenu texte brut'],
      howTo: [
        { title: 'Entrez le texte', description: 'Collez ou tapez du texte dans les deux champs.' },
        { title: 'Comparer', description: 'Cliquez sur Comparer pour surligner les différences.' },
        { title: 'Examiner les résultats', description: 'Les différences sont surlignées en couleur.' },
      ],
      faq: [{ q: 'Comment fonctionne la comparaison de texte ?', a: 'L\'outil compare deux textes caractère par caractère et surligne les ajouts, suppressions et modifications.' }],
    },
  },

  jsonEditor: {
    en: {
      features: ['View, edit, and format JSON data online', 'Validate JSON syntax with instant feedback', 'Convert JSON to CSV, XML, and YAML', 'Minify and beautify JSON with configurable indentation'],
      howTo: [
        { title: 'Enter JSON', description: 'Paste your JSON data or upload a JSON file.' },
        { title: 'Edit & Validate', description: 'Edit the JSON and use Validate to check for syntax errors.' },
        { title: 'Format or Convert', description: 'Beautify, minify, or convert your JSON to other formats.' },
      ],
      faq: [
        { q: 'What is JSON?', a: 'JSON (JavaScript Object Notation) is a lightweight data format used for data interchange in web applications and APIs.' },
        { q: 'Can I convert JSON to other formats?', a: 'Yes, PDFLove JSON Editor can convert JSON to CSV, XML, and YAML formats.' },
      ],
    },
    fr: {
      features: ['Visualisez, modifiez et formatez des données JSON en ligne', 'Validez la syntaxe JSON avec retour instantané', 'Convertissez JSON en CSV, XML et YAML', 'Minifiez et embellissez le JSON'],
      howTo: [
        { title: 'Entrez le JSON', description: 'Collez vos données JSON ou téléchargez un fichier JSON.' },
        { title: 'Modifier et valider', description: 'Modifiez le JSON et utilisez Valider pour vérifier la syntaxe.' },
        { title: 'Formater ou convertir', description: 'Embellissez, minifiez ou convertissez votre JSON.' },
      ],
      faq: [{ q: 'Qu\'est-ce que le JSON ?', a: 'Le JSON est un format de données léger utilisé pour l\'échange de données dans les applications web et les API.' }],
    },
  },

  editPdfMetadata: {
    en: {
      features: ['Edit PDF title, author, subject, and keywords', 'View existing PDF metadata before editing', 'Download the updated PDF with new metadata', 'Useful for organizing and cataloging PDF files'],
      howTo: [
        { title: 'Upload PDF', description: 'Select the PDF file whose metadata you want to edit.' },
        { title: 'Edit Properties', description: 'Modify the title, author, subject, and other fields.' },
        { title: 'Save & Download', description: 'Click Save to download the PDF with updated metadata.' },
      ],
      faq: [
        { q: 'What is PDF metadata?', a: 'PDF metadata includes properties like title, author, subject, and keywords that describe the document.' },
        { q: 'Why should I edit PDF metadata?', a: 'Proper metadata helps with document organization, searchability, and SEO when PDFs are published online.' },
      ],
    },
    fr: {
      features: ['Modifiez le titre, l\'auteur, le sujet et les mots-clés du PDF', 'Visualisez les métadonnées existantes avant modification', 'Téléchargez le PDF mis à jour', 'Utile pour organiser et cataloguer les fichiers PDF'],
      howTo: [
        { title: 'Téléchargez le PDF', description: 'Sélectionnez le fichier PDF dont vous souhaitez modifier les métadonnées.' },
        { title: 'Modifier les propriétés', description: 'Modifiez le titre, l\'auteur, le sujet et autres champs.' },
        { title: 'Enregistrer et télécharger', description: 'Cliquez sur Enregistrer pour télécharger le PDF avec les métadonnées mises à jour.' },
      ],
      faq: [{ q: 'Que sont les métadonnées PDF ?', a: 'Les métadonnées PDF incluent des propriétés comme le titre, l\'auteur, le sujet et les mots-clés.' }],
    },
  },

  annotatePdf: {
    en: {
      features: ['Add text annotations to PDF pages', 'Specify exact page numbers for annotations', 'Download the annotated PDF', 'Free online annotation tool — no installation'],
      howTo: [
        { title: 'Upload PDF', description: 'Select the PDF file you want to annotate.' },
        { title: 'Add Annotations', description: 'Enter your annotation text and specify the page number.' },
        { title: 'Save & Download', description: 'Click Save to download the annotated PDF.' },
      ],
      faq: [
        { q: 'Can I annotate PDF files online?', a: 'Yes, PDFLove lets you add text annotations to any page of your PDF document online for free.' },
        { q: 'What types of annotations can I add?', a: 'Currently you can add text annotations. Future updates may include highlighting, shapes, and drawing tools.' },
      ],
    },
    fr: {
      features: ['Ajoutez des annotations textuelles aux pages PDF', 'Spécifiez les numéros de page exacts', 'Téléchargez le PDF annoté', 'Outil d\'annotation en ligne gratuit'],
      howTo: [
        { title: 'Téléchargez le PDF', description: 'Sélectionnez le fichier PDF que vous souhaitez annoter.' },
        { title: 'Ajouter des annotations', description: 'Entrez votre texte d\'annotation et spécifiez le numéro de page.' },
        { title: 'Enregistrer et télécharger', description: 'Cliquez sur Enregistrer pour télécharger le PDF annoté.' },
      ],
      faq: [{ q: 'Puis-je annoter des fichiers PDF en ligne ?', a: 'Oui, PDFLove vous permet d\'ajouter des annotations textuelles à n\'importe quelle page de votre document PDF.' }],
    },
  },

  pdfEditor: {
    en: {
      features: ['Edit PDF documents online for free', 'Add text, images, shapes, and annotations', 'Fill and sign PDF forms', 'No software download required'],
      howTo: [
        { title: 'Upload PDF', description: 'Open your PDF file in the editor.' },
        { title: 'Edit Content', description: 'Use the toolbar to add text, images, shapes, or annotations.' },
        { title: 'Save & Download', description: 'Save your edits and download the modified PDF.' },
      ],
      faq: [
        { q: 'Can I edit PDF files online for free?', a: 'Yes, PDFLove provides a free online PDF editor where you can add text, images, and annotations to your PDF documents.' },
        { q: 'Do I need to install software?', a: 'No, the PDF editor works entirely in your web browser with no downloads or installations required.' },
      ],
    },
    fr: {
      features: ['Modifiez des documents PDF en ligne gratuitement', 'Ajoutez du texte, des images, des formes et des annotations', 'Remplissez et signez des formulaires PDF', 'Aucun téléchargement de logiciel requis'],
      howTo: [
        { title: 'Téléchargez le PDF', description: 'Ouvrez votre fichier PDF dans l\'éditeur.' },
        { title: 'Modifier le contenu', description: 'Utilisez la barre d\'outils pour ajouter du texte, des images, des formes ou des annotations.' },
        { title: 'Enregistrer et télécharger', description: 'Enregistrez vos modifications et téléchargez le PDF modifié.' },
      ],
      faq: [{ q: 'Puis-je modifier des fichiers PDF en ligne gratuitement ?', a: 'Oui, PDFLove propose un éditeur PDF en ligne gratuit.' }],
    },
  },

  home: {
    en: {
      features: [
        'All-in-one PDF toolkit with 20+ free tools',
        'Convert PDF to Word, Excel, JPG, PNG, PPT, and more',
        'Merge, split, annotate, and edit PDF documents',
        'Secure — all processing happens in your browser',
        'No registration or email required',
      ],
      howTo: [
        { title: 'Choose a Tool', description: 'Select the PDF tool you need from our comprehensive collection.' },
        { title: 'Upload Your File', description: 'Drag and drop or click to upload your PDF or document.' },
        { title: 'Download Result', description: 'Your file is processed instantly. Download the result with one click.' },
      ],
      faq: [
        { q: 'Is PDFLove completely free?', a: 'Yes, all PDF tools on PDFLove are 100% free with no hidden costs, subscriptions, or registration required.' },
        { q: 'Are my files safe?', a: 'All file processing happens locally in your browser. Your files are never uploaded to or stored on our servers.' },
        { q: 'What PDF tools are available?', a: 'PDFLove offers 20+ tools including PDF conversion (Word, Excel, JPG, PNG, PPT, etc.), merge, split, annotate, edit metadata, and more.' },
        { q: 'Do I need to create an account?', a: 'No, PDFLove works without any registration. Simply visit the site and start using any tool immediately.' },
      ],
    },
    fr: {
      features: [
        'Boîte à outils PDF tout-en-un avec plus de 20 outils gratuits',
        'Convertissez PDF en Word, Excel, JPG, PNG, PPT et plus',
        'Fusionnez, divisez, annotez et modifiez des documents PDF',
        'Sécurisé — tout le traitement se fait dans votre navigateur',
        'Aucune inscription ni e-mail requis',
      ],
      howTo: [
        { title: 'Choisissez un outil', description: 'Sélectionnez l\'outil PDF dont vous avez besoin dans notre collection complète.' },
        { title: 'Téléchargez votre fichier', description: 'Glissez-déposez ou cliquez pour télécharger votre PDF ou document.' },
        { title: 'Téléchargez le résultat', description: 'Votre fichier est traité instantanément. Téléchargez le résultat en un clic.' },
      ],
      faq: [
        { q: 'PDFLove est-il entièrement gratuit ?', a: 'Oui, tous les outils PDF sur PDFLove sont 100 % gratuits sans frais cachés, abonnements ou inscription.' },
        { q: 'Mes fichiers sont-ils en sécurité ?', a: 'Tout le traitement se fait localement dans votre navigateur. Vos fichiers ne sont jamais téléchargés ni stockés sur nos serveurs.' },
        { q: 'Quels outils PDF sont disponibles ?', a: 'PDFLove propose plus de 20 outils incluant la conversion PDF, la fusion, la division, l\'annotation, la modification des métadonnées et plus encore.' },
      ],
    },
  },
};
